/**
 * Build a Cursor rule (.mdc) file from LLM package metadata.
 * The rule gives the IDE/AI project-specific context (exports, use cases, conventions).
 */

import type { LLMPackageJson, ExportMeta } from './types.js';

function isStringArray(x: unknown): x is string[] {
  return Array.isArray(x) && x.every((v) => typeof v === 'string');
}

function formatExport(name: string, info: ExportMeta): string {
  const parts = [name];
  if (info.hook) parts.push('(Hook)');
  if (info.description) parts.push('—', info.description);
  if (info.params) parts.push('Params:', info.params);
  if (info.returns) parts.push('Returns:', info.returns);
  if (info.example) parts.push('Example:', info.example);
  return '- **' + parts[0] + '**' + (parts.length > 1 ? ': ' + parts.slice(1).join(' ') : '');
}

/**
 * Build the body of a .mdc rule: project context for Cursor/AI.
 */
export function buildRuleMdcContent(meta: LLMPackageJson): string {
  const lines: string[] = [];

  const desc = meta.summary ?? meta.description ?? `Package ${meta.name}.`;
  lines.push('## Project context', '');
  lines.push(desc, '');

  if (meta.whenToUse) {
    lines.push('### When to use', '', meta.whenToUse, '');
  }

  if (meta.reasonToUse && meta.reasonToUse.length > 0) {
    lines.push('### Reasons to use', '');
    for (const r of meta.reasonToUse) lines.push('- ' + r);
    lines.push('');
  }

  if (meta.useCases && meta.useCases.length > 0) {
    lines.push('### Use cases', '');
    for (const u of meta.useCases) lines.push('- ' + u);
    lines.push('');
  }

  if (meta.keywords && meta.keywords.length > 0) {
    lines.push('### Keywords', '', meta.keywords.join(', '), '');
  }

  if (meta.frameworks && meta.frameworks.length > 0) {
    lines.push('### Frameworks', '', meta.frameworks.join(', '), '');
  }

  if (meta.sideEffects && meta.sideEffects.length > 0) {
    lines.push('### Side effects', '');
    for (const s of meta.sideEffects) lines.push('- ' + s);
    lines.push('');
  }

  const exportEntries = Object.entries(meta.exports).sort(([a], [b]) => a.localeCompare(b));
  if (exportEntries.length > 0) {
    lines.push('### Exports', '');
    for (const [name, info] of exportEntries) {
      lines.push(formatExport(name, info));
    }
    lines.push('');
  }

  if (meta.hooks && meta.hooks.length > 0) {
    lines.push('### Hooks', '', meta.hooks.join(', '), '');
  }

  if (meta.documentation) {
    lines.push('### Documentation', '', meta.documentation, '');
  }

  if (meta.relatedPackages && meta.relatedPackages.length > 0) {
    lines.push('### Related packages', '', meta.relatedPackages.join(', '), '');
  }

  const ext = meta.extensions;
  if (ext && typeof ext === 'object') {
    const capabilities = ext.capabilities;
    const integration = ext.integration;
    if (isStringArray(capabilities) && capabilities.length > 0) {
      lines.push('### Capabilities', '');
      for (const c of capabilities) lines.push('- ' + c);
      lines.push('');
    }
    if (isStringArray(integration) && integration.length > 0) {
      lines.push('### Integration (IDE/tooling)', '');
      for (const i of integration) lines.push('- ' + i);
      lines.push('');
    }
  }

  lines.push('---', '');
  lines.push(`*Generated by ${meta.generatedBy} (mode: ${meta.mode}). Use \`llm.package.json\` / \`llm.package.txt\` for full metadata.*`);

  return lines.join('\n').trimEnd() + '\n';
}

/**
 * Build full .mdc file with optional frontmatter (description, globs).
 */
export function buildRuleMdc(meta: LLMPackageJson, options?: { globs?: string[] }): string {
  const description = meta.summary ?? meta.description ?? `Context for ${meta.name}`;
  const body = buildRuleMdcContent(meta);

  const globs = options?.globs ?? ['**/*.ts', '**/*.tsx', '**/*.js', '**/*.mjs', '**/*.cjs'];
  const frontmatter = [
    '---',
    `description: "${description.replace(/"/g, '\\"')}"`,
    `globs: ${JSON.stringify(globs)}`,
    '---',
    '',
  ].join('\n');

  return frontmatter + body;
}
